import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { PLANS } from '../mock/mockData';
import api from '../lib/api';
import { Copy, Check, ArrowRight, Info, AlertTriangle } from 'lucide-react';

const METHODS = [
  { id: 'jazzcash', name: 'JazzCash', account: '0321-8848400', label: 'Mobile Account' },
  { id: 'easypaisa', name: 'Easypaisa', account: '0321-8848400', label: 'Mobile Account' },
  { id: 'nayapay', name: 'NayaPay', account: '0321-8848400', label: 'NayaPay Number' },
  { id: 'sadapay', name: 'SadaPay', account: '0321-8848400', label: 'SadaPay Number' },
  { id: 'bank', name: 'Bank Transfer', account: 'PK17 UNIL 0109 0002 9601 9263', label: 'IBAN (UBL)' }
];

export default function Billing() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const plan = PLANS.find(p => p.id === planId) || PLANS[1];

  const [method, setMethod] = useState('jazzcash');
  const [sentTo, setSentTo] = useState('jazzcash');
  const [amount, setAmount] = useState(plan.priceMonthly);
  const [ref, setRef] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const doCopy = (t, id) => {
    navigator.clipboard?.writeText(t);
    setCopied(id);
    setTimeout(() => setCopied(''), 1200);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!sentTo) return setError('Please select where you sent the payment.');
    if (!amount || Number(amount) <= 0) return setError('Please enter a valid payment amount.');
    if (!ref.trim()) return setError('Please enter your transaction reference.');
    try {
      await api.post('/payments', {
        plan_id: plan.id,
        sent_to: sentTo,
        amount: Number(amount),
        reference: ref.trim(),
      });
      setSubmitted(true);
      setTimeout(() => navigate('/dashboard'), 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to submit payment');
    }
  };

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-cyan-300 mb-1">// manual billing</div>
          <h1 className="font-mono-display text-3xl text-white">Activate <span className="neon-orange">{plan.name}</span> plan</h1>
          <p className="text-zinc-400 mt-1">Send <span className="text-orange-300 font-mono-display">{plan.priceLabel}</span> via any method below, then submit your transaction reference.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-6">
          {METHODS.slice(0, 3).map(m => (
            <button key={m.id} onClick={() => setMethod(m.id)} className={`neon-card rounded p-4 text-left transition-all ${method === m.id ? 'border-orange-400/80 bg-orange-500/5' : ''}`}>
              <div className="text-xs uppercase tracking-widest text-cyan-300">{m.label}</div>
              <div className="font-mono-display text-lg text-white">{m.name}</div>
              <div className="flex items-center gap-2 mt-2">
                <code className="text-sm text-zinc-300">{m.account}</code>
                <span onClick={(e) => { e.stopPropagation(); doCopy(m.account, m.id); }} className="cursor-pointer">
                  {copied === m.id ? <Check className="w-3.5 h-3.5 text-cyan-300" /> : <Copy className="w-3.5 h-3.5 text-zinc-500 hover:text-cyan-300" />}
                </span>
              </div>
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {METHODS.slice(3).map(m => (
            <button key={m.id} onClick={() => setMethod(m.id)} className={`neon-card rounded p-4 text-left transition-all ${method === m.id ? 'border-orange-400/80 bg-orange-500/5' : ''}`}>
              <div className="text-xs uppercase tracking-widest text-cyan-300">{m.label}</div>
              <div className="font-mono-display text-lg text-white">{m.name}</div>
              <div className="flex items-center gap-2 mt-2">
                <code className="text-sm text-zinc-300">{m.account}</code>
                <span onClick={(e) => { e.stopPropagation(); doCopy(m.account, m.id); }} className="cursor-pointer">
                  {copied === m.id ? <Check className="w-3.5 h-3.5 text-cyan-300" /> : <Copy className="w-3.5 h-3.5 text-zinc-500 hover:text-cyan-300" />}
                </span>
              </div>
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="neon-card rounded-lg p-6">
          <h3 className="font-mono-display text-lg text-white mb-4">Submit Payment Proof</h3>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-cyan-300">Payment Sent To <span className="text-red-300">*</span></span>
              <select required value={sentTo} onChange={(e) => setSentTo(e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 focus:border-orange-400/70 rounded px-3 py-2 outline-none text-white">
                <option value="jazzcash">JazzCash</option>
                <option value="easypaisa">Easypaisa</option>
                <option value="nayapay">NayaPay</option>
                <option value="sadapay">SadaPay</option>
                <option value="bank">Bank Account</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-cyan-300">Payment Amount (PKR) <span className="text-red-300">*</span></span>
              <input required type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 1200" className="mt-1 w-full bg-black/40 border border-zinc-800 focus:border-orange-400/70 rounded px-3 py-2 outline-none text-white tabular" />
            </label>
          </div>

          <label className="block mb-4">
            <span className="text-xs uppercase tracking-widest text-cyan-300">Transaction Reference <span className="text-red-300">*</span></span>
            <input required value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. JC-89271-AA" className="mt-1 w-full bg-black/40 border border-zinc-800 focus:border-orange-400/70 rounded px-3 py-2 outline-none text-white" />
          </label>

          {error && (
            <div className="mb-4 flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5" />{error}
            </div>
          )}
          {submitted && (
            <div className="mb-4 flex items-start gap-2 text-sm text-cyan-200 bg-cyan-400/10 border border-cyan-400/30 rounded p-3">
              <Check className="w-4 h-4 mt-0.5" />Payment submitted. Admin will verify and activate soon. Redirecting...
            </div>
          )}
          <div className="flex items-center gap-3">
            <button disabled={submitted} className="btn-neon">Submit for Review <ArrowRight className="w-4 h-4" /></button>
            <Link to="/pricing" className="btn-ghost">Cancel</Link>
          </div>

          {/* Manual Billing Instructions moved here */}
          <div className="mt-8 pt-6 border-t border-zinc-800">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-orange-300" />
              <h4 className="font-mono-display text-base text-orange-300">Manual Billing Instructions</h4>
            </div>
            <ol className="list-decimal list-inside text-sm text-zinc-300 space-y-2 leading-relaxed">
              <li>Send the plan amount (PKR) to one of our JazzCash, Easypaisa, NayaPay, SadaPay or Bank accounts above.</li>
              <li>Select the method you sent the payment to and enter the exact amount sent.</li>
              <li>Submit your unique transaction reference number from your wallet / bank receipt.</li>
              <li>Admin manually verifies &amp; activates your subscription within a few hours.</li>
              <li>On expiry, your account automatically reverts to the Free plan (10 slots).</li>
            </ol>
          </div>
        </form>
      </section>
    </Layout>
  );
}
